// restyle(prefab, style, id): derive a culture variant of ANY structure prefab,
// the 3 drawn city centres (house / tavern / castle) included, through one
// pipeline (docs/02-systems/building-styles.md). The style table lists the ops
// per prefab class (`style.ops`); this file implements each op. Pure and
// deterministic.
//
// Ops: recolor, team-accents, crown (generic, every kind), and centre (the
// city-centre classes: the base geometry is re-composed from the style's module
// vocabulary in buildings_styled.ts, because house / tavern / castle differ in
// structure per culture, not just in colour).
//
// 1. Recolour by colour *family* (not by voxel `type`, which most extras leave
//    unset): roof red -> style roof, tan planks -> style wall, dark wood ->
//    style trim, grey stone -> style base, glass, gold. Shading is kept by
//    carrying the source lightness over its family mean, so detail survives.
//    Functional colours (crystals, flames, crops, water) are left alone.
// 2. Team accent: the perimeter of the ground layer becomes team-coloured
//    (same device as the city-centre plinth) and a small pennant tops the roof.
// 3. Culture crown: a style finial on the highest point.
//
// Footprint never changes (recolour + a few voxels above the roof), so every
// variant fits the tile exactly like its base prefab.

import { buildCentre } from './buildings_styled.js';
import {
  type BuildingStyle,
  CENTRE_PREFAB_TIER,
  type RestyleOp,
  type StylePalette,
} from './styles.js';
import type { BlockVoxel, StructurePrefab } from './types.js';

interface Hsl {
  readonly h: number;
  readonly s: number;
  readonly l: number;
}

function toHsl(hex: string): Hsl {
  const r = Number.parseInt(hex.slice(1, 3), 16) / 255;
  const g = Number.parseInt(hex.slice(3, 5), 16) / 255;
  const b = Number.parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
  else if (max === g) h = ((b - r) / d + 2) * 60;
  else h = ((r - g) / d + 4) * 60;
  return { h, s, l };
}

function fromHsl({ h, s, l }: Hsl): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  const to = (v: number) =>
    Math.round(Math.min(1, Math.max(0, v + m)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

type Family = 'roof' | 'plank' | 'wood' | 'stone' | 'glass' | 'gold';

/** Reference lightness per family (the mean of the shared prefabs' colours). */
const FAMILY_L: Readonly<Record<Family, number>> = {
  roof: 0.27,
  plank: 0.65,
  wood: 0.3,
  stone: 0.5,
  glass: 0.7,
  gold: 0.5,
};

function familyOf(hex: string): Family | null {
  const { h, s, l } = toHsl(hex);
  if (s < 0.12 && l > 0.15 && l < 0.85) return 'stone';
  if (h >= 40 && h <= 60 && s > 0.8 && l > 0.4 && l < 0.6) return 'gold';
  if ((h < 12 || h > 345) && s > 0.8 && l < 0.35) return 'roof';
  if (h >= 190 && h <= 250 && s > 0.3 && l > 0.5) return 'glass';
  if (h >= 20 && h <= 50 && s > 0.2 && l >= 0.5 && l < 0.85) return 'plank';
  if (h >= 15 && h <= 40 && s > 0.2 && l >= 0.12 && l < 0.5) return 'wood';
  return null;
}

const TARGET: Readonly<Record<Family, keyof StylePalette>> = {
  roof: 'roof',
  plank: 'wall',
  wood: 'trim',
  stone: 'base',
  glass: 'glass',
  gold: 'accent',
};

function recolor(hex: string, p: StylePalette): string {
  const fam = familyOf(hex);
  if (!fam) return hex;
  const target = toHsl(p[TARGET[fam]]);
  const src = toHsl(hex);
  const l = Math.min(0.97, Math.max(0.04, target.l + (src.l - FAMILY_L[fam]) * 0.8));
  return fromHsl({ h: target.h, s: target.s, l });
}

const TEAM_LIT = '#d2d2d2';
const TEAM_FOLD = '#a8a8a8';

/** Style crown: voxels added above the apex (dx, dy, dz) relative to it. */
const CROWNS: Readonly<Record<StyleCrown, ReadonlyArray<readonly [number, number, number]>>> = {
  // Gilded orb on a short spike.
  orb: [
    [0, 1, 0],
    [0, 2, 0],
  ],
  // Crossed dragon horns.
  horns: [
    [0, 1, 0],
    [-1, 2, 0],
    [1, 2, 0],
    [0, 2, -1],
    [0, 2, 1],
  ],
  // Tall gold spike with curled tips.
  spike: [
    [0, 1, 0],
    [0, 2, 0],
    [0, 3, 0],
  ],
  none: [],
};
type StyleCrown = 'orb' | 'horns' | 'spike' | 'none';

const STYLE_CROWN: Readonly<Record<string, StyleCrown>> = {
  'hip-low': 'orb',
  'gable-steep': 'horns',
  'swept-tiers': 'spike',
  shared: 'none',
};

type V = BlockVoxel;

interface OpCtx {
  readonly style: BuildingStyle;
  readonly id: string;
}

type Op = (blocks: readonly V[], ctx: OpCtx) => V[];

const OPS: Readonly<Record<RestyleOp, Op>> = {
  recolor: (blocks, { style }) =>
    blocks.map((b) => {
      const color = recolor(b.color, style.palette);
      return color === b.color ? b : { ...b, color };
    }),

  // Ground-layer perimeter -> team plinth.
  'team-accents': (blocks) => {
    const ground = blocks.filter((b) => b.y === 0);
    if (ground.length === 0) return [...blocks];
    const xs = ground.map((b) => b.x);
    const zs = ground.map((b) => b.z);
    const [x0, x1, z0, z1] = [Math.min(...xs), Math.max(...xs), Math.min(...zs), Math.max(...zs)];
    return blocks.map((b) =>
      b.y === 0 && (b.x === x0 || b.x === x1 || b.z === z0 || b.z === z1)
        ? { x: b.x, y: 0, z: b.z, color: TEAM_LIT, team: true }
        : b,
    );
  },

  // Style finial on the apex plus a team pennant above it (kept inside the footprint).
  crown: (blocks, { style }) => {
    const p = style.palette;
    const out: V[] = [...blocks];
    const reach = Math.max(...out.flatMap((b) => [Math.abs(b.x), Math.abs(b.z)]));
    const push = (v: V) => {
      if (Math.abs(v.x) <= reach && Math.abs(v.z) <= reach) out.push(v);
    };
    const top = Math.max(...out.map((b) => b.y));
    const apex = out
      .filter((b) => b.y === top)
      .sort(
        (a, b) =>
          Math.abs(a.x) + Math.abs(a.z) - (Math.abs(b.x) + Math.abs(b.z)) || a.x - b.x || a.z - b.z,
      )[0];
    if (!apex) return out;
    const crown = CROWNS[STYLE_CROWN[style.roof] ?? 'none'];
    for (const [dx, dy, dz] of crown) {
      push({ x: apex.x + dx, y: apex.y + dy, z: apex.z + dz, color: p.accent });
    }
    const h = crown.reduce((m, [, dy]) => Math.max(m, dy), 0);
    for (const v of [
      { x: apex.x, y: apex.y + h + 1, z: apex.z, color: p.trim },
      { x: apex.x, y: apex.y + h + 2, z: apex.z, color: p.trim },
      { x: apex.x + 1, y: apex.y + h + 2, z: apex.z, color: TEAM_LIT, team: true },
      { x: apex.x + 1, y: apex.y + h + 1, z: apex.z, color: TEAM_FOLD, team: true },
    ]) {
      push(v);
    }
    return out;
  },

  // City centres: re-composed from the style's modules (plinth, walls, roof, landmark, banners).
  centre: (blocks, { style, id }) => {
    const tier = CENTRE_PREFAB_TIER[id];
    return tier && style.id !== 'neutral' ? [...buildCentre(style.id, tier).blocks] : [...blocks];
  },
};

/** Prefab id is optional only for ad-hoc prefabs; registered prefabs always pass theirs. */
export function restyle(base: StructurePrefab, style: BuildingStyle, id = ''): StructurePrefab {
  const ops = id in CENTRE_PREFAB_TIER ? style.ops.centre : style.ops.default;
  let blocks: readonly V[] = base.blocks;
  for (const op of ops) blocks = OPS[op](blocks, { style, id });
  const seen = new Map<string, V>();
  for (const b of blocks) seen.set(`${b.x},${b.y},${b.z}`, b);
  const name =
    id in CENTRE_PREFAB_TIER
      ? buildCentre(style.id as never, CENTRE_PREFAB_TIER[id] as never).name
      : `${base.name} (${style.name})`;
  return { name, blocks: [...seen.values()] };
}
